import { DeviceLimitationContext } from '@context/DeviceLimitationContext';
import {
  getIsStargateEnabledForGoal,
  getSocketConstants
} from '@api/selectors/constants';
import fetchCall from '@api/fetchers';
import { DeviceLimitationAPIError } from '@utils/customErrors';
import { captureException } from '@utils/sentry';
import { useCallback, useContext } from 'react';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  DEVICE_LIMITATION_LOGOUT_OTHERS,
  DEVICE_LIMITATION_RETRY_CLICKED,
  DEVICE_LIMITATION_RETRY_FAILED,
  DEVICE_LIMITATION_RETRY_SUCCESS,
  DEVICE_LIMITATION_API_TIME_OUT
} from '@constants/segment';
import { segment } from '@actions/analytics';
import { AnyArgVoidReturnFunction } from 'types/utils';
import useConstants from '@api/hooks/useConstants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getStargateJwtToken } from '@api/selectors/auth';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';

const API_TIMEOUT_ERROR = 'API_TIMEOUT_ERROR';

const is4XXError = (statusCode) => {
  if (statusCode) return statusCode >= 400 && statusCode < 500;
};

export enum FeatureObjectType {
  CLASS = 1,
  QUIZ = 2,
  COMBAT = 3,
  TEST = 4
}

const checkDeviceAccessApiTimeout = (timeout: number) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(API_TIMEOUT_ERROR));
    }, timeout);
  });
};

async function logoutOtherDevicesAPICall(
  stargateDomain: string,
  stargateJWTToken: string
) {
  try {
    await fetchCall(`https://${stargateDomain}/api/device/logout/`, {
      requestType: 'POST',
      useOriginalURL: true,
      useAuthToken: false,
      headers: { Authorization: `Bearer ${stargateJWTToken}` }
    });
  } catch (error) {
    captureException(new DeviceLimitationAPIError(error));
  }
}

const fetchShouldAllowUserActivity = async (
  objectType: FeatureObjectType,
  stargateDomain: string,
  stargateJWTToken: string,
  goalUID: string
) => {
  return new Promise((resolve, reject) => {
    fetchCall(
      `https://${stargateDomain}/api/device/access/?goal_uid=${goalUID}&object_type=${objectType}`,
      {
        useOriginalURL: true,
        useAuthToken: false,
        headers: { Authorization: `Bearer ${stargateJWTToken}` }
      }
    )
      .then((res: any) => {
        if (res?.error && !is4XXError(res?.status || res?.statusCode))
          reject(res?.error);
        else resolve(res?.accessPrimaryFeature && true);
      })
      .catch((err) => reject(err));
  });
};

const fetchShouldAllowFeatureAccess = async (
  objectType: FeatureObjectType,
  stargateDomain: string,
  stargateJWTToken: string,
  goalUID: string,
  deviceAccessTimeout: number
) => {
  const shouldAllow = await Promise.race([
    checkDeviceAccessApiTimeout(deviceAccessTimeout),
    fetchShouldAllowUserActivity(
      objectType,
      stargateDomain,
      stargateJWTToken,
      goalUID
    )
  ]);

  return shouldAllow;
};

const useCheckFeatureLimit = (
  objectType: FeatureObjectType,
  onSuccessFn: AnyArgVoidReturnFunction
) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const goalUID = useSelectedGoalUID();
  const stargateJWTToken = useMyInfo(getStargateJwtToken);
  const { showNudge, hideNudge, updateHandlers, setCheckForAccessState } =
    useContext(DeviceLimitationContext);

  const goalSegmentData = useGoalEventProps({ goalUID });

  const { deviceAccessDomain: stargateDomain = '', deviceAccessTimeout } =
    useConstants(getSocketConstants) ?? {};
  const isStargateEnabled = useConstants(getIsStargateEnabledForGoal(goalUID));

  return useCallback(
    async (...featureAccessArg: any[]) => {
      let allowAccessToFeature =
        false || !stargateJWTToken || !isStargateEnabled || !isLoggedIn;
      const handleSuccess = () => {
        hideNudge();
        onSuccessFn(...featureAccessArg);
      };

      if (isStargateEnabled && stargateJWTToken && isLoggedIn) {
        if (objectType === FeatureObjectType.CLASS) {
          try {
            const shouldAllow = await fetchShouldAllowFeatureAccess(
              objectType,
              stargateDomain,
              stargateJWTToken,
              goalUID,
              deviceAccessTimeout
            );
            allowAccessToFeature = shouldAllow as boolean;
          } catch (err) {
            allowAccessToFeature = true;
            if (err && err?.message === API_TIMEOUT_ERROR) {
              segment.track(DEVICE_LIMITATION_API_TIME_OUT, goalSegmentData);
            }
          }
        }
      }

      if (allowAccessToFeature) {
        handleSuccess();
      } else {
        updateHandlers({
          logoutAllOtherDevice: async () => {
            segment.track(DEVICE_LIMITATION_LOGOUT_OTHERS, goalSegmentData);
            try {
              await logoutOtherDevicesAPICall(stargateDomain, stargateJWTToken);
            } catch (err) {
              // Right now don't catch error and unblock user
            }
            handleSuccess();
          },
          checkAgainForAccess: async () => {
            segment.track(DEVICE_LIMITATION_RETRY_CLICKED, goalSegmentData);
            setCheckForAccessState('checking');
            setTimeout(async () => {
              try {
                const shouldAllow = await fetchShouldAllowFeatureAccess(
                  objectType,
                  stargateDomain,
                  stargateJWTToken,
                  goalUID,
                  deviceAccessTimeout
                );
                setCheckForAccessState('success');
                if (shouldAllow) {
                  segment.track(
                    DEVICE_LIMITATION_RETRY_SUCCESS,
                    goalSegmentData
                  );
                  handleSuccess();
                } else {
                  segment.track(
                    DEVICE_LIMITATION_RETRY_FAILED,
                    goalSegmentData
                  );
                  setCheckForAccessState('waiting');
                }
              } catch (err) {
                if (err && err?.message === API_TIMEOUT_ERROR) {
                  segment.track(
                    DEVICE_LIMITATION_API_TIME_OUT,
                    goalSegmentData
                  );
                  handleSuccess();
                } else {
                  segment.track(
                    DEVICE_LIMITATION_RETRY_FAILED,
                    goalSegmentData
                  );
                  setCheckForAccessState('waiting');
                }
              }
            }, 1000);
          }
        });
        showNudge();
      }
    },
    [
      isStargateEnabled,
      hideNudge,
      onSuccessFn,
      objectType,
      stargateDomain,
      deviceAccessTimeout,
      stargateJWTToken,
      goalUID,
      updateHandlers,
      showNudge,
      goalSegmentData,
      setCheckForAccessState,
      isLoggedIn
    ]
  );
};

export default useCheckFeatureLimit;
